import { Fragment, useEffect } from 'react';

import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import type { GetServerSideProps } from 'next/types';

import { getSourceEventParams, useAmplitude } from '@crehana/analytics';
import { useMe } from '@crehana/auth';
import { FeatureFlagProvider } from '@crehana/feature-flags/react';
import { useTranslation } from '@crehana/i18n';
import {
  getG11nAndTranslationsFromServer,
  initializeApolloClient,
} from '@crehana/next';
import { customLocalStorage } from '@crehana/utils';

import { WrapperMainFooter } from '@/shared/components/wrapper-main-footer';
import usePersistQueryParams from '@/shared/hooks/usePersistQueryParams';
import { Header } from '@/views/B2BLanding/components';

import { AMPLITUDE_TRACKS } from '../../events';
import localeViewEn from '../../i18n/localeEn';
import localeViewEs from '../../i18n/localeEs';
import localeViewPt from '../../i18n/localePt';
import { ORDER_BY } from './constants';
import GET_DOWNLOADABLE_RESOURCES from './graphql/GetDownloadableResources.graphql';
import GET_RESOURCE_TYPES from './graphql/GetResourceTypes.graphql';
import {
  GetDownloadableResources,
  GetDownloadableResourcesVariables,
} from './graphql/types/GetDownloadableResources';
import { GetResourceTypes } from './graphql/types/GetResourceTypes';
import localeEn from './i18n/localeEn';
import localeEs from './i18n/localeEs';
import localePt from './i18n/localePt';
import Body from './sections/Body';
import Hero from './sections/Hero';

const PAGE_KEY = 'b2b-downloadable-detail';

const Downloadables = () => {
  usePersistQueryParams();
  const { me } = useMe();
  const { t, addResourceBundle } = useTranslation();
  const { logEvent } = useAmplitude();
  const router = useRouter();
  const organizationOriginalId = me?.organization?.originalId;

  addResourceBundle(
    {
      es: { [PAGE_KEY]: { ...localeEs.es, ...localeViewEs.es } },
      pt: { [PAGE_KEY]: { ...localePt.pt, ...localeViewPt.pt } },
      en: { [PAGE_KEY]: { ...localeEn.en, ...localeViewEn.en } },
    },
    { injectOnce: true },
  );

  const seoTitle = t('SEO_TITLE', { defaultValue: localeEs.es.SEO_TITLE });
  const seoDescription = t('SEO_DESCRIPTION', {
    defaultValue: localeEs.es.SEO_DESCRIPTION,
  });

  useEffect(() => {
    const hasCompletedForm = router.query?.hasCompletedForm === 'true';
    const detailResourceUrl = customLocalStorage.getItem('detailResourceUrl');
    const downloadUrlB2B = customLocalStorage.getItem('downloadUrlB2B');

    if (hasCompletedForm && detailResourceUrl) {
      customLocalStorage.removeItem('detailResourceUrl');
      customLocalStorage.removeItem('downloadUrlB2B');
      window.open(downloadUrlB2B!);
      router.push(`/business/descargables/${detailResourceUrl}`);
      return;
    }

    logEvent({
      event: AMPLITUDE_TRACKS.LANDING_PAGE_VIEWED,
      data: {
        ...getSourceEventParams(),
        Product: 'B2B Downloadables',
        pageSlug: 'descargables',
        LandingName: 'Downloadables',
      },
    });
  }, [logEvent]);

  return (
    <Fragment>
      <FeatureFlagProvider
        organizationId={organizationOriginalId!}
        flags={[['LEARNING_HIDE_BANNER_DOWNLOAD_APP', 'V5']]}
      >
        <NextSeo
          title={seoTitle}
          description={seoDescription}
          openGraph={{ title: seoTitle, description: seoDescription }}
        />
        <Header />
        <Hero />
        <Body />
        <WrapperMainFooter />
      </FeatureFlagProvider>
    </Fragment>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ req }) => {
  const apolloClient = initializeApolloClient({
    headers: req.headers,
  });
  const g11Data = await getG11nAndTranslationsFromServer({
    headers: req.headers,
    pageKey: PAGE_KEY,
    defaultTranslations: {
      es: { [PAGE_KEY]: { ...localeEs.es, ...localeViewEs.es } },
      pt: { [PAGE_KEY]: { ...localePt.pt, ...localeViewPt.pt } },
      en: { [PAGE_KEY]: { ...localeEn.en, ...localeViewEn.en } },
    },
  });

  await apolloClient.query<
    GetDownloadableResources,
    GetDownloadableResourcesVariables
  >({
    query: GET_DOWNLOADABLE_RESOURCES,
    variables: {
      first: 12,
      orderBy: ORDER_BY.RECENT,
    },
  });

  await apolloClient.query<GetResourceTypes>({
    query: GET_RESOURCE_TYPES,
  });

  return {
    props: {
      initialApolloState: apolloClient.cache.extract(),
      ...g11Data,
    },
  };
};

export default Downloadables;
